<template>
  <form @submit.prevent="submit">
    <b-card>
      <fields :form="form" ungrouped />
      <fields :form="form" group="payment_methods" @input="onChangeDayNightFieldsComponentType"/>
    </b-card>

    <div v-if="loaded">
      <b-card :title="$t('vehicles.new_layout_zone_to_zone_pricing')">
        <div v-if="selectedVehicles.length" class="mt-4">
          <div v-if="model.is_fixed" class="vehicle-pricing-table table-responsive">
            <hint-text :value="$t('vehicles.attributes.pricing.new_layout_route_price_notify')" />
            <table class="table datatable-save-state dataTable no-footer">
              <thead>
                <tr>
                  <th></th>
                  <th v-for="vehicle in selectedVehicles" :key="vehicle.id">{{ getVehicleName(vehicle.id) }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{{ $t('vehicles.attributes.pricing.fixed_price.label') }}</td>
                  <td v-for="vehicle in selectedVehicles" :key="vehicle.id">
                    <b-form-input
                      v-model.number="vehiclePricingFor(vehicle.id).fixed_price"
                      type="number"
                      step="0.01"
                      min="0"
                      size="sm" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else-if="byRequest" class="vehicle-pricing-table table-responsive">
            <hint-text :value="$t('vehicles.attributes.pricing.new_layout_route_price_auction_notify')" />
            <table class="table no-footer">
              <thead>
                <tr>
                  <th></th>
                  <th v-for="vehicle in selectedVehicles" :key="vehicle.id">{{ getVehicleName(vehicle.id) }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{{ $t('vehicles.attributes.pricing.by_request_percent.label') }}</td>
                  <td v-for="vehicle in selectedVehicles" :key="vehicle.id">
                    <b-form-input
                      v-model.number="vehiclePricingFor(vehicle.id).by_request_percent"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      size="sm" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else class="vehicle-pricing-table table-responsive">
            <hint-text :value="$t('vehicles.attributes.pricing.new_layout_route_price_notify')" />
            <table class="table no-footer per-km">
              <thead>
                <tr>
                  <th></th>
                  <th v-for="vehicle in selectedVehicles" :key="vehicle.id">{{ getVehicleName(vehicle.id) }}</th>
                  <th class="text-center" style="width: 40px;"></th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{{ $t('vehicles.attributes.pricing.minimum_price.label') }}</td>
                  <td v-for="vehicle in selectedVehicles" :key="vehicle.id">
                    <b-form-input
                      v-model.number="vehiclePricingFor(vehicle.id).minimum_price"
                      type="number"
                      step="0.01"
                      min="0"
                      size="sm"
                      :placeholder="$t('vehicles.attributes.pricing.minimum_price.label')" />
                  </td>
                  <td></td>
                </tr>
                <tr v-for="(tier, index) in displayTiers" :key="index">
                  <td>
                    <span>{{ tierFrom(firstVehicleId, index) }}-</span>
                    <b-form-input
                      :value="tierTo(firstVehicleId, index)"
                      type="number"
                      step="1"
                      min="0"
                      :max="tierToMax(index)"
                      size="sm"
                      class="d-inline-block tier-to-input"
                      @input="setTierTo(index, $event)" /> {{ $t('vehicles.attributes.km.label') }}
                  </td>
                  <td v-for="vehicle in selectedVehicles" :key="vehicle.id">
                    <b-form-input
                      :value="tierPrice(vehicle.id, index)"
                      type="number"
                      step="0.01"
                      min="0"
                      size="sm"
                      :placeholder="$t('vehicles.attributes.pricing.new_layout_route_price_per_km')"
                      @input="setTierPrice(vehicle.id, index, $event)" />
                  </td>
                  <td class="text-center">
                    <i
                      v-if="displayTiers.length > 1"
                      class="icon-trash text-danger cursor-pointer"
                      @click="removeTier(index)"
                    ></i>
                  </td>
                </tr>
              </tbody>
            </table>
            <b-button variant="outline-secondary" size="sm" class="mt-2" @click="addNewTier">
              {{ $t('general.add_new') }}
            </b-button>
          </div>
        </div>
      </b-card>
    </div>

    <!--    <input v-model="noConflictCheck" type="hidden" name="no-conflict-check" />-->
    <b-button type="submit" variant="primary">
      {{ $t('general.save') }}
    </b-button>
    <b-button type="button" variant="light" class="ml-2" @click="cancel">
      {{ $t('general.cancel') }}
    </b-button>
  </form>
</template>

<script>
import isPlainObject from 'lodash-es/isPlainObject'
import { mapState } from 'vuex'
import { AdminApi, ResourceFormMixin } from 'stylemix-vue-admin'
import PerDayPrices from '../vehicles/PerDayPrices.vue'
import HintText from '../components/HintText.vue'

export default {
  name: 'ZoneToZonePriceForm',

  components: {
    PerDayPrices,
    HintText,
  },

  mixins: [ResourceFormMixin],

  props: {
    id: { type: [Number, String], required: false, default: null },
    vehicleId: { type: [Number, String], required: false, default: null },
    editId: { type: [Number, String], required: false, default: null },
    apiInterface: { type: Object, required: true },
    rideType: { type: String, required: true },
  },

  data() {
    return {
      base: 'route-pricing',
      days: [
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday',
      ],
      periods: [
        'day',
        'night',
      ],
      loaded: false,
      defaultTiers: [
        { from: 0, to: 35, price: null, label: this.$t('vehicles.attributes.pricing.tier_0_35.label') },
        { from: 35, to: 50, price: null, label: this.$t('vehicles.attributes.pricing.tier_35_50.label') },
        { from: 50, to: 80, price: null, label: this.$t('vehicles.attributes.pricing.tier_50_80.label') },
        { from: 80, to: 100, price: null, label: this.$t('vehicles.attributes.pricing.tier_80_100.label') },
        { from: 100, to: 500, price: null, label: this.$t('vehicles.attributes.pricing.tier_100_500.label') },
      ]
    }
  },

  computed: {
    ...mapState('zones', ['currentDivision']),
    form() {
      return this
    },
    divisionId() {
      return (
        this.currentDivision?.id
        ?? this.model?.zone_a?.division_id
        ?? this.model?.zone_b?.division_id
        ?? null
      )
    },
    effectiveId() {
      return this.id ?? this.editId
    },
    updating() {
      return !!this.effectiveId
    },
    byRequest() {
      return !!(this.model.pricing && this.model.pricing.z2z_by_request)
    },
    selectedVehicles() {
      let ids = this.model.vehicle_ids
      if (!ids || !ids.length) {
        const vehicles = this.model.vehicles
        ids = Array.isArray(vehicles) ? vehicles.map(v => v.id) : []
      }
      return ids.map(id => ({ id }))
    },
    firstVehicleId() {
      const ids = this.model.vehicle_ids || []
      return ids[0] ?? this.model.vehicles?.[0]?.id ?? null
    },
    displayTiers() {
      const vid = this.firstVehicleId
      if (!vid) return this.defaultTiers
      const key = String(vid)
      const p = this.model.vehicle_pricing?.[key]
      const t = p?.tiers
      if (!t || !t.length) return this.defaultTiers
      return t
    },
  },

  watch: {
    effectiveId() {
      this.load()
    },
    rideType: {
      handler() {
        if (this.loaded && this.model) {
          this.$set(this.model, 'ride_type', this.rideType)
          this.applyAllDivisionVehicles()
        }
      },
    },
    divisionId: {
      handler(newVal, oldVal) {
        if (newVal && this.loaded && oldVal !== undefined && newVal !== oldVal) {
          this.applyAllDivisionVehicles()
        }
      },
    },
    'model.vehicle_ids': {
      handler(newVal) {
        this.ensureVehiclePricing(Array.isArray(newVal) ? newVal : [])
      },
      deep: true,
    },
    'model.is_fixed'(enabled) {
      if (enabled && this.byRequest) {
        if (!this.model.pricing) {
          this.$set(this.model, 'pricing', {})
        }
        this.$set(this.model.pricing, 'z2z_by_request', false)
      }
    },
    'model.pricing.z2z_by_request'(enabled) {
      if (enabled && this.model.is_fixed) {
        this.$set(this.model, 'is_fixed', false)
      }
    },
  },

  mounted() {
    this.load()
  },

  methods: {
    api() {
      return this.apiInterface
    },

    getVehicleName(vehicleId) {
      if (this.model.vehicles && Array.isArray(this.model.vehicles)) {
        const v = this.model.vehicles.find(x => x.id === vehicleId)
        if (v) return v.name || v.id
      }
      return vehicleId
    },

    stripVehicleIdsField() {
      const list = this.fields.list
      const i = list.findIndex(f => f.attribute === 'vehicle_ids')
      if (i !== -1) {
        list.splice(i, 1)
      }
    },

    /**
     * All vehicles of the current division for this ride type (same as if vehicle_ids selected all).
     */
    async applyAllDivisionVehicles() {
      const divisionId = this.divisionId
      const rideType = this.model.ride_type || this.rideType
      if (!divisionId) {
        return
      }
      try {
        const vehiclesApi = new AdminApi('vehicles', {
          division_id: divisionId,
          per_page: 500,
        })
        const payload = await vehiclesApi.all()
        const rows = Array.isArray(payload.data) ? payload.data : []
        const filtered = rows.filter(v => {
          const types = v.ride_types || []
          return types.includes(rideType) || types.includes(String(rideType))
        })
        const ids = filtered.map(v => v.id)
        this.$set(this.model, 'vehicle_ids', ids)
        this.$set(
          this.model,
          'vehicles',
          filtered.map(v => ({ id: v.id, name: v.name })),
        )
        this.ensureVehiclePricing(ids)
      } catch (e) {
        /* eslint-disable-next-line no-console */
        console.error('ZoneToZonePriceForm: failed to load division vehicles', e)
      }
    },

    ensureVehiclePricing(vehicleIds) {
      if (!this.model.vehicle_pricing) {
        this.$set(this.model, 'vehicle_pricing', {})
      }
      vehicleIds.forEach(id => {
        const key = String(id)
        if (!this.model.vehicle_pricing[key]) {
          this.$set(this.model.vehicle_pricing, key, {
            fixed_price: null,
            minimum_price: null,
            by_request_percent: null,
            tiers: JSON.parse(JSON.stringify(this.defaultTiers)),
          })
        }
      })
    },

    vehiclePricingFor(vehicleId) {
      this.ensureVehiclePricing(this.model.vehicle_ids || [])
      const key = String(vehicleId)
      if (!this.model.vehicle_pricing[key]) {
        this.$set(this.model.vehicle_pricing, key, {
          fixed_price: null,
          minimum_price: null,
          by_request_percent: null,
          tiers: JSON.parse(JSON.stringify(this.defaultTiers)),
        })
      }
      return this.model.vehicle_pricing[key]
    },

    tierPrice(vehicleId, tierIndex) {
      const p = this.vehiclePricingFor(vehicleId)
      if (!p.tiers) p.tiers = JSON.parse(JSON.stringify(this.defaultTiers))
      if (!p.tiers[tierIndex]) p.tiers[tierIndex] = { ...this.defaultTiers[tierIndex] }
      return p.tiers[tierIndex].price
    },

    setTierPrice(vehicleId, tierIndex, value) {
      const p = this.vehiclePricingFor(vehicleId)
      if (!p.tiers) p.tiers = JSON.parse(JSON.stringify(this.defaultTiers))
      if (!p.tiers[tierIndex]) p.tiers[tierIndex] = { ...this.defaultTiers[tierIndex] }
      const num = value === '' || value == null ? null : parseFloat(value)
      this.$set(p.tiers[tierIndex], 'price', isNaN(num) ? null : num)
    },

    tierFrom(vehicleId, tierIndex) {
      if (!vehicleId || tierIndex === 0) return 0
      const p = this.vehiclePricingFor(vehicleId)
      const prev = p.tiers?.[tierIndex - 1]
      return prev ? (prev.to ?? 0) : 0
    },

    tierTo(vehicleId, tierIndex) {
      if (!vehicleId) return this.defaultTiers[tierIndex]?.to ?? 0
      const p = this.vehiclePricingFor(vehicleId)
      const t = p.tiers?.[tierIndex]
      return t?.to ?? this.defaultTiers[tierIndex]?.to ?? 0
    },

    tierToMax(tierIndex) {
      const lastIndex = this.displayTiers.length - 1
      if (tierIndex >= lastIndex) return 10000
      const vehicleId = this.firstVehicleId
      if (!vehicleId) return 500
      const p = this.vehiclePricingFor(vehicleId)
      const nextTier = p.tiers?.[tierIndex + 1]
      return nextTier?.to ?? this.defaultTiers[tierIndex + 1]?.to ?? 500
    },

    addNewTier() {
      const vehicleIds = this.model.vehicle_ids || []
      if (!vehicleIds.length) return
      this.ensureVehiclePricing(vehicleIds)
      const firstId = this.firstVehicleId
      const p = this.vehiclePricingFor(firstId)
      const tiers = p.tiers || []
      const lastTier = tiers[tiers.length - 1]
      const lastTo = lastTier ? (lastTier.to ?? 500) : 500
      const newTier = { from: lastTo, to: lastTo + 100, price: null }
      vehicleIds.forEach((vid) => {
        const vp = this.vehiclePricingFor(vid)
        if (!vp.tiers) vp.tiers = JSON.parse(JSON.stringify(this.defaultTiers))
        vp.tiers.push({ ...newTier })
      })
    },

    removeTier(tierIndex) {
      if (this.displayTiers.length <= 1) return
      const vehicleIds = this.model.vehicle_ids || []
      const removedFrom = this.tierFrom(this.firstVehicleId, tierIndex)
      vehicleIds.forEach((vid) => {
        const vp = this.vehiclePricingFor(vid)
        if (!vp.tiers) return
        vp.tiers.splice(tierIndex, 1)
        if (vp.tiers[tierIndex]) {
          this.$set(vp.tiers[tierIndex], 'from', removedFrom)
        }
      })
    },

    setTierTo(tierIndex, value) {
      const num = value === '' || value == null ? null : parseInt(value, 10)
      if (num === null || isNaN(num)) return
      const vehicleIds = this.model.vehicle_ids || []
      const firstId = this.firstVehicleId
      const minVal = tierIndex === 0 ? 0 : this.tierFrom(firstId, tierIndex)
      const maxVal = this.tierToMax(tierIndex)
      const clamped = Math.max(minVal, Math.min(maxVal, num))
      vehicleIds.forEach((vehicleId) => {
        const p = this.vehiclePricingFor(vehicleId)
        if (!p.tiers) p.tiers = JSON.parse(JSON.stringify(this.defaultTiers))
        if (!p.tiers[tierIndex]) p.tiers[tierIndex] = { ...this.defaultTiers[tierIndex] }
        this.$set(p.tiers[tierIndex], 'to', clamped)
        if (tierIndex < this.displayTiers.length - 1 && p.tiers[tierIndex + 1]) {
          this.$set(p.tiers[tierIndex + 1], 'from', clamped)
        }
      })
    },

    /**
     * Ensure vehicle_pricing is keyed by vehicle ID (not array index) before save.
     * Builds { [vehicleId]: pricing } from vehicle_ids.
     */
    normalizeVehiclePricingBeforeSave() {
      const vehicleIds = this.model.vehicle_ids || []
      if (!Array.isArray(vehicleIds) || vehicleIds.length === 0) {
        this.$set(this.model, 'vehicle_pricing', {})
        return
      }
      const normalized = {}
      vehicleIds.forEach((vehicleId) => {
        const key = String(vehicleId)
        const existing = this.model.vehicle_pricing?.[key] ?? this.model.vehicle_pricing?.[vehicleId]
        normalized[key] = existing ?? {
          fixed_price: null,
          minimum_price: null,
          by_request_percent: null,
          tiers: JSON.parse(JSON.stringify(this.defaultTiers)),
        }
      })
      this.$set(this.model, 'vehicle_pricing', normalized)
    },

    submit() {
      this.save()
        .then(result => {
          this.$toast.success(result.message)
        })
        .catch(({ data }) => {
          if (data && data.conflicting) {
            this.$alert.warning(data.message, '', {
              showCancelButton: true,
              confirmButtonText: this.$t('general.save'),
              preConfirm: force => {
                if (!force) {
                  return
                }

                this.apiInterface.setParameter('no-conflict-check', 1)
                this.save().finally(() => {
                  this.apiInterface.removeParameter('no-conflict-check')
                })
              },
            })
          }
        })
    },
    cancel() {
      this.$emit('cancel')
    },
    load() {
      this.loaded = false
      this.loadFormWithEffectiveId().then(async () => {
        this.loaded = true
        this.model.ride_type = this.model.ride_type ?? this.rideType

        this.stripVehicleIdsField()
        this.normalizeVehiclePricingOnLoad()
        if (this.model.is_fixed && this.model.pricing?.z2z_by_request) {
          this.$set(this.model.pricing, 'z2z_by_request', false)
        }
        await this.applyAllDivisionVehicles()
        this.ensureVehiclePricing(this.model.vehicle_ids || [])

        this.setDayNightFieldsComponentType(this.model.pricing?.days_metric)
      })
    },

    /**
     * Convert vehicle_pricing from array [0=>{...}, 1=>{...}] to object { [vehicleId]: {...} }
     * when backend returns it keyed by index instead of vehicle ID.
     */
    normalizeVehiclePricingOnLoad() {
      if (!this.model.vehicle_pricing) {
        this.$set(this.model, 'vehicle_pricing', {})
        return
      }
      if (!Array.isArray(this.model.vehicle_pricing)) {
        return
      }
      const vehicleIds = this.model.vehicle_ids || []
      if (!vehicleIds.length) {
        const vehicles = this.model.vehicles || []
        vehicleIds.push(...(Array.isArray(vehicles) ? vehicles.map(v => v.id) : []))
      }
      const obj = {}
      vehicleIds.forEach((vehicleId, index) => {
        const pricing = this.model.vehicle_pricing[index]
        obj[String(vehicleId)] = pricing && typeof pricing === 'object'
          ? { ...pricing }
          : {
            fixed_price: null,
            minimum_price: null,
            by_request_percent: null,
            tiers: JSON.parse(JSON.stringify(this.defaultTiers)),
          }
      })
      this.$set(this.model, 'vehicle_pricing', obj)
    },
    loadFormWithEffectiveId() {
      const effectiveId = this.effectiveId
      const promise = effectiveId
        ? this.api().edit(effectiveId)
        : this.api().create()
      const fullPromise = promise.then((result) => {
        this.model = isPlainObject(result.data) ? result.data : {}
        this.setFields(result.fields)
        return result
      })
      return this.$uiBlocker(fullPromise, this.$el)
    },
    save() {
      this.errors.clear()
      this.normalizeVehiclePricingBeforeSave()
      const formData = this.formData(this.model)
      // vehicle_ids was removed from the PHP form fields list but the API still needs it to run syncVehiclesWithPricing.
      const ids = this.model.vehicle_ids || []
      if (Array.isArray(ids)) {
        ids.forEach((id) => {
          formData.append('vehicle_ids[]', id)
        })
      }
      const effectiveId = this.effectiveId
      const promise = effectiveId
        ? this.api().update(effectiveId, formData)
        : this.api().store(formData)
      const fullPromise = promise
        .then((result) => {
          this.onSaved(result)
          return result
        })
        .catch((response) => {
          this.onSaveError(response)
          return Promise.reject(response)
        })
      return this.$uiBlocker(fullPromise, this.$el)
    },



    setDayNightFieldsComponentType(selectedDaysMetric) {
      let dayNightPricingAttrs = this.fields.list.filter(field =>
        this.days.some(day =>
          this.periods.some(period => field.attribute === `pricing.days.${day}.${period}`)
        )
      );

      if (selectedDaysMetric === 'fixed' || selectedDaysMetric === 'surcharge') {
        dayNightPricingAttrs.map(field => field.component = 'price-in-selected-currency-field')
      } else {
        dayNightPricingAttrs.map(field => field.component = 'number-field')
      }
    },

    onChangeDayNightFieldsComponentType(fieldData) {
      this.setDayNightFieldsComponentType(fieldData.value)
    },

  },
}
</script>

<style scoped>
.vehicle-pricing-table >>> .table th {
  //white-space: nowrap;
}
.vehicle-pricing-table >>> input.form-control {
  width: 70px;
}
.vehicle-pricing-table >>> input.tier-to-input {
  width: 70px;
}
</style>
